import React from 'react';
import {
  Box,
  Button,
  Paper,
  Stack,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  Popover,
  Divider,
  TextField,
  IconButton,
  InputAdornment,
} from '@mui/material';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import CircularProgress from '@mui/material/CircularProgress';
import dayjs, { Dayjs } from 'dayjs';
import { useTranslate } from 'react-admin';
import type { DashboardV2TabKey, QuickRange } from '../hooks/useDashboardV2Data';
import { PermissionButton } from 'src/routes/components/permission-button';


const TAB_HEIGHT = 40;
const TAB_ITEM_HEIGHT = 32; // TAB_HEIGHT - padding(4*2)
const RANGE_BTN_HEIGHT = 36;
const TIMEFRAME_HEIGHT = 32;

function SegmentedTabs(props: {
  value: DashboardV2TabKey;
  onChange?: (v: DashboardV2TabKey) => void;
  tabs: Array<{ value: DashboardV2TabKey; label: string }>;
}) {
  const { value, onChange, tabs } = props;

  return (
    <Paper
      variant="outlined"
      sx={{
        height: TAB_HEIGHT,
        px: 0.5,
        py: 0.5,
        borderRadius: 2,
        bgcolor: '#F4F4F5',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        borderColor: 'grey.200',
      }}
    >
      {tabs.map((t) => {
        const active = value === t.value;
        return (
          <Button
            key={t.value}
            onClick={() => onChange ? onChange(t.value) : null}
            disableElevation
            variant="text"
            sx={{
              height: TAB_ITEM_HEIGHT,
              minWidth: 0,
              px: 2,
              borderRadius: 1.5,
              textTransform: 'none',
              fontSize: 14,
              fontWeight: 600,
              color: 'text.primary',
              bgcolor: active ? 'common.white' : 'transparent',
              boxShadow: active ? '0px 1px 2px rgba(0,0,0,0.10)' : 'none',
              '&:hover': {
                bgcolor: active ? 'common.white' : 'grey.200',
              },
            }}
          >
            {t.label}
          </Button>
        );
      })}
    </Paper>
  );
}

function clampOrder(a: Dayjs, b: Dayjs) {
  if (!a || !b) return { from: a, to: b };
  return a.isAfter(b) ? { from: b, to: a } : { from: a, to: b };
}

function formatRangeLabel(from: Dayjs, to: Dayjs) {
  return `${from.format('YYYY/MM/DD')} - ${to.format('YYYY/MM/DD')}`;
}

export function DashboardV2Header(props: {
  title: string;

  tab: DashboardV2TabKey;
  onTabChange: (v: DashboardV2TabKey) => void;

  from: Dayjs;
  to: Dayjs;
  onFromChange: (v: Dayjs) => void;
  onToChange: (v: Dayjs) => void;

  /** 新增：当用户在 Timeframe 完成选择（尤其是选完 To 关闭时）触发一次 */
  onApplyTimeframe?: (from: Dayjs, to: Dayjs) => void;

  range: QuickRange;
  onRangeChange: (v: QuickRange) => void;

  onExport?: () => void;
  exporting?: boolean;
}) {
  const t = useTranslate();
  const {
    title,
    tab,
    onTabChange,
    from,
    to,
    onFromChange,
    onToChange,
    onApplyTimeframe,
    range,
    onRangeChange,
    onExport,
    exporting,
  } = props;

  // Popover
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  const [tmpFrom, setTmpFrom] = React.useState<Dayjs>(from);
  const [tmpTo, setTmpTo] = React.useState<Dayjs>(to);

  React.useEffect(() => {
    setTmpFrom(from);
    setTmpTo(to);
  }, [from, to]);

  const handleOpen = (e: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(e.currentTarget);
  };
  const handleClose = () => setAnchorEl(null);

  const commit = (nextFrom: Dayjs, nextTo: Dayjs, close?: boolean) => {
    const ordered = clampOrder(nextFrom, nextTo);

    const fromFixed = ordered.from.startOf('day');
    const toFixed = ordered.to.endOf('day');

    if (range !== 'custom') onRangeChange('custom');

    onFromChange(fromFixed);
    onToChange(toFixed);
    onApplyTimeframe?.(fromFixed, toFixed);

    if (close) handleClose();
  };


  // 输入框显示：始终以“当前已应用的 from/to”为准
  const displayLabel = formatRangeLabel(from, to);

  return (
    <Box sx={{ width: '100%' }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={1.5}
        sx={{ width: '100%', flexWrap: 'wrap' }}
      >
        <Typography variant="h4" sx={{ fontWeight: 900, lineHeight: 1.1 }}>
          {title}
        </Typography>

        <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="flex-end" flexWrap="wrap">

          <TextField
            size="small"
            value={displayLabel}
            onClick={handleOpen}
            label={t('dashboardV2.timeframe', { _: 'Timeframe' })}
            InputProps={{
              readOnly: true,
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={handleOpen}>
                    <CalendarMonthIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              width: 280,
              '& .MuiInputBase-root': {
                height: TIMEFRAME_HEIGHT,
                fontSize: 14,
                borderRadius: 2,
                bgcolor: 'common.white',
              },
              '& .MuiOutlinedInput-notchedOutline': { borderColor: 'grey.300' },
              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'grey.400' },
            }}
          />

          <Popover
            open={open}
            anchorEl={anchorEl}
            onClose={handleClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            PaperProps={{
              sx: {
                mt: 1,
                p: 2,
                width: 520,
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'grey.200',
                boxShadow: '0px 10px 30px rgba(0,0,0,0.12)',
              },
            }}
          >
            <Typography sx={{ fontSize: 14, fontWeight: 700 }}>
              {t('dashboardV2.timeframe', { _: 'Timeframe' })}
            </Typography>
            <Divider sx={{ my: 1.5 }} />

            <Stack direction="row" spacing={1.5} alignItems="center">
              <DatePicker
                label={t('dashboardV2.from', { _: 'From' })}
                value={tmpFrom}
                onChange={(v) => {
                  if (!v) return;
                  setTmpFrom(v);
                  commit(v, tmpTo, false);
                }}
                closeOnSelect={false}
                slotProps={{
                  textField: {
                    size: 'small',
                    sx: {
                      width: 240,
                      '& .MuiInputBase-root': {
                        height: RANGE_BTN_HEIGHT,
                        fontSize: 14,
                        borderRadius: 2,
                        bgcolor: 'common.white',
                      },
                    },
                  },
                }}
              />

              <DatePicker
                label={t('module.dashboardV2.to', { _: 'To' })}
                value={tmpTo}
                onChange={(v) => {
                  if (!v) return;
                  setTmpTo(v);
                  commit(tmpFrom, v, false);
                }}
                onAccept={(v) => {
                  if (!v) return;
                  setTmpTo(v);
                  commit(tmpFrom, v, true);
                }}
                closeOnSelect
                slotProps={{
                  textField: {
                    size: 'small',
                    sx: {
                      width: 240,
                      '& .MuiInputBase-root': {
                        height: RANGE_BTN_HEIGHT,
                        fontSize: 14,
                        borderRadius: 2,
                        bgcolor: 'common.white',
                      },
                    },
                  },
                }}
              />
            </Stack>
          </Popover>

          {/* Quick range */}
          <ToggleButtonGroup
            exclusive
            value={range}
            onChange={(_, v) => v && onRangeChange(v)}
            sx={{
              height: RANGE_BTN_HEIGHT,
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              p: 0,
              bgcolor: 'transparent',
              border: 'none',
              '& .MuiToggleButtonGroup-grouped': {
                height: RANGE_BTN_HEIGHT,
                minHeight: RANGE_BTN_HEIGHT,
                px: 2, // 左右 16px（2 * 8）
                py: 1, // 上下 8px（1 * 8）
                borderRadius: 0.75,
                textTransform: 'none',
                fontSize: 14,
                fontWeight: 500,
                lineHeight: `${RANGE_BTN_HEIGHT - 2}px`,
                border: '1px solid #E4E4E7 !important',
                borderLeft: '1px solid #E4E4E7',
                bgcolor: 'common.white',
                boxSizing: 'border-box',
                color: 'text.primary',
                '&:hover': { bgcolor: 'grey.100' },
                '&.Mui-selected': {
                  bgcolor: '#605E68',
                  color: '#FFFFFF',
                  borderColor: '#E4E4E7',
                  '&:hover': { bgcolor: '#605E68' },
                },
              },
            }}
          >
            <ToggleButton value="24h">24h</ToggleButton>
            <ToggleButton value="7d">7d</ToggleButton>
            <ToggleButton value="30d">30d</ToggleButton>
            <ToggleButton value="all">{t('common.all', { _: 'All' })}</ToggleButton>
          </ToggleButtonGroup>

          {/* Export */}

          <PermissionButton resource="perpetuals" action="export">
            <Button
              variant="contained"
              onClick={onExport}
              disabled={!onExport || exporting}
              startIcon={exporting ? <CircularProgress size={16} sx={{ color: 'common.white' }} /> : null}
              sx={{
                height: RANGE_BTN_HEIGHT,
                borderRadius: 0.75,
                px: 2.2,
                textTransform: 'none',
                fontSize: 14,
                fontWeight: 700,
                bgcolor: '#18181B',
                '&:hover': { bgcolor: 'grey.900' },
              }}
            >
              {t('module.dashboardV2.csvExport', { _: 'CSV Export' })}
            </Button>
          </PermissionButton>
        </Stack>
      </Stack>

      <Box sx={{ mt: 1.25 }}>
        <SegmentedTabs
          value={tab}
          onChange={() => { }}
          // onChange={onTabChange}
          tabs={[
            { value: 'perpetuals', label: t('module.dashboardV2.tabs.perpetuals', { _: 'Perpetuals' }) },
            { value: 'meme', label: t('module.vdashboardV2.tabs.meme', { _: 'Meme' }) },
            { value: 'user', label: t('module.dashboardV2.tabs.user', { _: 'User Management' }) },
          ]}
        />
      </Box>
    </Box>
  );
}
